import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, MegaphoneIcon, MonitorIcon, DollarSignIcon, UsersIcon, HeadphonesIcon, CrownIcon, BuildingIcon, ShieldIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const nicheIcons = [<CrownIcon className="w-5 h-5" data-id="element-1020" data-mp="3kc5akx0zfqyd" />, <MonitorIcon className="w-5 h-5" data-id="element-1021" data-mp="3kd6wxxng7zve" />, <DollarSignIcon className="w-5 h-5" data-id="element-1022" data-mp="3oco2er2vkx0k" />, <TrendingUpIcon className="w-5 h-5" data-id="element-1023" data-mp="3odquh2bxp5i2" />, <MegaphoneIcon className="w-5 h-5" data-id="element-1024" data-mp="3oal0p9sd1fg0" />, <UsersIcon className="w-5 h-5" data-id="element-1025" data-mp="3tbs8h8ui6emo" />, <BuildingIcon className="w-5 h-5" data-id="element-1026" data-mp="3tapn1gfy94e5" />, <ShieldIcon className="w-5 h-5" data-id="element-1027" data-mp="1x32fvz9dcy4" />, <GlobeIcon className="w-5 h-5" data-id="element-1028" data-mp="1y4pyzxgnrwq" />, <HeadphonesIcon className="w-5 h-5" data-id="element-1029" data-mp="1zfp2mfyobhi" />];
const nicheStyles = [{
  color: 'text-navy-700',
  bgColor: 'bg-navy-100',
  borderColor: 'border-navy-300'
}, {
  color: 'text-violet-600',
  bgColor: 'bg-violet-100',
  borderColor: 'border-violet-300'
}, {
  color: 'text-emerald-600',
  bgColor: 'bg-emerald-100',
  borderColor: 'border-emerald-300'
}, {
  color: 'text-blue-600',
  bgColor: 'bg-blue-100',
  borderColor: 'border-blue-300'
}, {
  color: 'text-pink-600',
  bgColor: 'bg-pink-100',
  borderColor: 'border-pink-300'
}, {
  color: 'text-primary-600',
  bgColor: 'bg-primary-100',
  borderColor: 'border-primary-300'
}, {
  color: 'text-orange-600',
  bgColor: 'bg-orange-100',
  borderColor: 'border-orange-300'
}, {
  color: 'text-amber-600',
  bgColor: 'bg-amber-100',
  borderColor: 'border-amber-300'
}, {
  color: 'text-rose-600',
  bgColor: 'bg-rose-100',
  borderColor: 'border-rose-300'
}, {
  color: 'text-teal-600',
  bgColor: 'bg-teal-100',
  borderColor: 'border-teal-300'
}];
// Positions stay as-is (mostly international role names)
const nichePositions = [['CEO / Генеральний директор', 'COO / Операційний директор', 'CFO / Фінансовий директор', 'CTO / Технічний директор', 'CMO / Директор з маркетингу', 'CHRO / Директор з персоналу', 'CPO / Директор з продукту', 'CSO / Директор зі стратегії'], ['CTO / VP of Engineering', 'Tech Lead / Team Lead', 'Frontend Developer (React, Angular, Vue)', 'Backend Developer (Node, Go, Java, Python)', 'Full‑stack Developer', 'DevOps / SRE Engineer', 'QA Engineer / QA Lead', 'Product Manager / Product Owner', 'UI/UX Designer', 'Data Engineer / Data Scientist', 'Mobile Developer (iOS, Android)', 'Solutions Architect', 'Scrum Master / Agile Coach', 'IT Project Manager', 'Cybersecurity Engineer', 'Cloud Engineer (AWS, Azure, GCP)'], ['CFO / Financial Director', 'Financial Analyst', 'Chief Accountant', 'Auditor / Internal Auditor', 'Financial Controller', 'Risk Manager', 'Compliance Officer', 'Treasury Manager'], ['Commercial Director', 'Head of Sales', 'Key Account Manager', 'Business Development Manager', 'Sales Director', 'Sales Representative', 'Head of Partnerships', 'Regional Sales Manager'], ['CMO / Marketing Director', 'Digital Marketing Manager', 'Brand Manager', 'Performance Marketing Manager', 'PR Director', 'Content Marketing Manager', 'Growth Manager', 'Head of Communications'], ['CHRO / HR Director', 'HR Business Partner', 'Head of Recruitment', 'C&B Manager', 'HR Analyst', 'L&D Manager', 'Employer Brand Manager', 'People Operations Manager'], ['COO / Operations Director', 'Supply Chain Director', 'Logistics Manager', 'Procurement Director', 'Warehouse Manager', 'Operations Manager', 'Procurement Manager', 'Fleet Manager'], ['General Counsel / Legal Director', 'Corporate Lawyer', 'Compliance Manager', 'Labor Law Attorney', 'IP Lawyer', 'Litigation Lawyer', 'Legal Counsel', 'Data Privacy Officer (DPO)'], ['E‑commerce Director', 'Category Manager', 'Merchandiser', 'Retail Operations Manager', 'Marketplace Manager', 'Head of Online Sales', 'CRM Manager', 'Trade Marketing Manager'], ['Personal Assistant to CEO', 'Office Manager', 'Executive Assistant', 'Project Coordinator', 'Customer Success Manager', 'Head of Administration', 'Facilities Manager', 'Business Analyst']];
export function NichesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const niches = t.niches.categories.map((cat, i) => ({
    title: cat.title,
    icon: nicheIcons[i],
    positions: nichePositions[i],
    ...nicheStyles[i]
  }));
  const active = niches[activeIndex];
  return <section className="py-20 lg:py-28 bg-navy-50/50 overflow-hidden" id="niches" data-id="element-1030" data-mp="jiv243pyf9mg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-1031" data-mp="eoh37rc32kd1">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1032" data-mp="ap86qepns8ds">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1033" data-mp="te55aus39n1t">
            {t.niches.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1034" data-mp="oi7oyogtsgp1">
            {t.niches.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1035" data-mp="3962oq3sxv4lk">
            {t.niches.subtitle}
          </p>
          <p className="mt-3 text-sm text-navy-400 max-w-xl mx-auto" data-id="element-1036" data-mp="3aosxx7b3ii1k">
            {t.niches.note}
          </p>
        </motion.div>

        {/* Mobile: horizontal scroll tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="lg:hidden mb-6 -mx-4 px-4" data-id="element-1037" data-mp="31nmf2rkz4v55">
          <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide" data-id="element-1038" data-mp="2ixd4ouf19x2d">
            {niches.map((niche, index) => <button key={niche.title} onClick={() => setActiveIndex(index)} className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${activeIndex === index ? `${niche.bgColor} ${niche.color} shadow-sm` : 'bg-white text-navy-500 border border-navy-200'}`} data-id="element-1039" data-mp="2myggn5tyn8yb" data-mpi={index}>
                {niche.icon}
                {niche.title}
              </button>)}
          </div>
        </motion.div>

        {/* Mobile: positions panel */}
        <div className="lg:hidden" data-id="element-1040" data-mp="1zaa8g6omrwd9">
          <AnimatePresence mode="wait" data-id="element-1041" data-mp="1z8yvphd7jea0">
            <motion.div key={activeIndex} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.2
          }} className="bg-white rounded-2xl border border-navy-100 p-6" data-id="element-1042" data-mp="2da1hdt0aimke">
              <div className="flex items-center gap-3 mb-5" data-id="element-1043" data-mp="2rz7glgz4ocsa">
                <div className={`w-10 h-10 rounded-xl ${active.bgColor} ${active.color} flex items-center justify-center`} data-id="element-1044" data-mp="2s0s6gtahz7jt">
                  {active.icon}
                </div>
                <div data-id="element-1045" data-mp="79ynj3oy831m">
                  <h3 className="font-bold text-navy-900" data-id="element-1046" data-mp="c6yrn928mvq3">{active.title}</h3>
                  <p className="text-xs text-navy-400" data-id="element-1047" data-mp="c4uee0ig8kd5">
                    {active.positions.length} {t.niches.positions_label}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1048" data-mp="3fpibbn6uot9a">
                {active.positions.map(pos => <div key={pos} className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-navy-50/50 text-sm text-navy-600" data-id="element-1049" data-mp="3oodip2lms9e6">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active.bgColor}`} data-id="element-1050" data-mp="3tgwvvygep2ig" />
                    {pos}
                  </div>)}
                <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg border border-dashed border-navy-200 text-sm text-navy-400 italic" data-id="element-1051" data-mp="30wewhnkn8tm9">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-navy-200" data-id="element-1052" data-mp="2oh8bu2fqeera" />
                  {t.niches.other_positions}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop: side-by-side tabs + panel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="hidden lg:grid lg:grid-cols-12 gap-6" data-id="element-1053" data-mp="2agnyvmwhzd5p">
          <div className="col-span-4 space-y-1.5" data-id="element-1054" data-mp="1roldjy8wv8p4">
            {niches.map((niche, index) => <button key={niche.title} onClick={() => setActiveIndex(index)} className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 group ${activeIndex === index ? `bg-white shadow-md border ${niche.borderColor}` : 'hover:bg-white/60 border border-transparent'}`} data-id="element-1055" data-mp="1ms4lwu2i6c7a" data-mpi={index}>
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200 ${activeIndex === index ? `${niche.bgColor} ${niche.color}` : 'bg-navy-100/60 text-navy-400 group-hover:bg-navy-100 group-hover:text-navy-500'}`} data-id="element-1056" data-mp="29y1stbu8l9cc">
                  {niche.icon}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1057" data-mp="1r7tvxbh4rf4l">
                  <div className={`text-sm font-semibold truncate transition-colors ${activeIndex === index ? 'text-navy-900' : 'text-navy-600 group-hover:text-navy-800'}`} data-id="element-1058" data-mp="1r72c0xwwb5u5">
                    {niche.title}
                  </div>
                  <div className="text-xs text-navy-400" data-id="element-1059" data-mp="oepxwve77org">
                    {niche.positions.length} {t.niches.positions_label}
                  </div>
                </div>
                {activeIndex === index && <motion.div layoutId="niche-indicator" className={`w-1.5 h-8 rounded-full ${niche.bgColor}`} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }} data-id="element-1060" data-mp="ffuuts6gausd" />}
              </button>)}
          </div>

          <div className="col-span-8" data-id="element-1061" data-mp="ycm6pgjny7me">
            <AnimatePresence mode="wait" data-id="element-1062" data-mp="13ciezrze8l7r">
              <motion.div key={activeIndex} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="bg-white rounded-2xl border border-navy-100 shadow-sm p-8 min-h-[420px]" data-id="element-1063" data-mp="13bgs8q90maa7">
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-navy-100" data-id="element-1064" data-mp="1qrj0n3hq7zob">
                  <div className={`w-14 h-14 rounded-2xl ${active.bgColor} ${active.color} flex items-center justify-center`} data-id="element-1065" data-mp="1mpxh4zaosn55">
                    <div className="scale-125" data-id="element-1066" data-mp="1hxmehookan8w">{active.icon}</div>
                  </div>
                  <div data-id="element-1067" data-mp="1d0vzl4ohtxp2">
                    <h3 className="text-2xl font-extrabold text-navy-900" data-id="element-1068" data-mp="1cy05xsjohv6f">
                      {active.title}
                    </h3>
                    <p className="text-sm text-navy-400 mt-0.5" data-id="element-1069" data-mp="132gb79gup2j9">
                      {active.positions.length} {t.niches.positions_label}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-1070" data-mp="3aogd7wjfxx8s">
                  {active.positions.map((pos, i) => <motion.div key={pos} initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2,
                  delay: i * 0.03
                }} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-navy-50/40 border border-navy-100/50" data-id="element-1071" data-mp="3eo8s0qzfgzml" data-mpi={i}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active.bgColor}`} data-id="element-1072" data-mp="35pna8y1s5omv" />
                      <span className="text-sm font-medium text-navy-700" data-id="element-1073" data-mp="2873p1uh7j2bi">
                        {pos}
                      </span>
                    </motion.div>)}
                  <motion.div initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2,
                  delay: active.positions.length * 0.03
                }} className="flex items-center gap-3 py-3 px-4 rounded-xl border border-dashed border-navy-200 col-span-2" data-id="element-1074" data-mp="isc8si6jbnkl">
                    <span className="w-2 h-2 rounded-full flex-shrink-0 bg-navy-200" data-id="element-1075" data-mp="1bgzzita1z55j" />
                    <span className="text-sm text-navy-400 italic" data-id="element-1076" data-mp="16h59ery3qjlj">
                      {t.niches.other_positions}
                    </span>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-10 text-center" data-id="element-1077" data-mp="1nr951pjuthw1">
          <a href="#contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-600/30 hover:shadow-xl hover:shadow-primary-600/35 hover:-translate-y-0.5" data-id="element-1078" data-mp="150rdvpeo6ucs">
            {'\u0417\u0430\u043C\u043E\u0432\u0438\u0442\u0438 \u043F\u0456\u0434\u0431\u0456\u0440 \u0441\u043F\u0435\u0446\u0456\u0430\u043B\u0456\u0441\u0442\u0430'}
          </a>
        </motion.div>
      </div>
    </section>;
}